import { useCallback, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import {
  useKioskSession,
  useKioskSessionLocale
} from "~/stores/kiosk-session/hooks/use-kiosk-session";
import { kioskActions } from "~/stores/kiosk-session/actions";
import { priceFormatter } from "~/utils/price-formatter";
import { useKioskProducts } from "~/stores/kiosk-session/hooks/use-kiosk-products";
import { CardPaymentModal } from "~/components/kiosk/ui/modals/checkout/payment";
import { ModalLoadingGeneric } from "~/components/kiosk/ui/modals/loading/modal-loading-generic";
import { ModalError } from "~/components/kiosk/ui/modals/error/modal-error";
import { ModalOutOfStockContainer } from "~/components/kiosk/ui/modals/out-of-stock/modal-out-of-stock-container";
import { ModalOrderMinimumContainer } from "~/components/kiosk/ui/modals/order-minimum/modal-order-minimum-container";
import { DisplayErrorMessage } from "~/components/display-error-message";
import { ApiErrorCodesEnum, isApiErrorCode } from "~/api/error";
import { useFreedomPayOrderStatus } from "~/api/orders/hooks/use-create-order";
import { useDebounce } from "~/hooks/use-debounce";
import { toast } from "~/components/hooks/use-toast";
import { useCancelPoiPayment } from "~/hooks/freedompay/use-cancel-poi-payment";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";

export const KioskBasketPaymentFreedomPayPage = () => {
  const navigate = useNavigate();
  const returnToBasket = useCallback(() => {
    kioskActions.session.resetOrderId();
    kioskActions.session.generateServiceId();
    navigate("/kiosk/basket");
  }, [navigate]);

  const orderId = useKioskSession((session) => session?.orderId);
  const locale = useKioskSessionLocale();

  const { unitTotalPayableInclVat, currency } = useKioskProducts();

  const { data: till } = useCurrentTill();

  // Orchestrate payment flow
  const { status, payload } = useFreedomPayOrderStatus();
  const { mutate: cancelPoiPayment } = useCancelPoiPayment();

  const hasReceiptModeAutomatic =
    till?.type === "kiosk" && till.config.receiptPrintMode === "automatic";

  const onCancel = () => {
    if (!orderId) {
      return;
    }
    if (status === "pending-poi-payment") {
      cancelPoiPayment();
    }

    toast({
      title: "Payment cancelled",
      description: "The payment has been cancelled",
      variant: "success"
    });

    returnToBasket();
  };

  const debouncedOnCancel = useDebounce(onCancel, 1000);

  // Same contract as the standard payment route: no mutation means nothing
  // is in flight, not "still creating the order".
  useEffect(() => {
    if (status === "idle") {
      returnToBasket();
    }
  }, [status, returnToBasket]);

  if (status === "idle") {
    return <ModalLoadingGeneric />;
  }

  if (status === "error") {
    if (
      isApiErrorCode(
        payload.error,
        ApiErrorCodesEnum.OFFER_PRODUCTS_NOT_AVAILABLE
      )
    ) {
      return (
        // FreedomPay runs POI payment in parallel with order creation and voids
        // the POI when the order is OOS, so a paid card was always reversed here.
        <ModalOutOfStockContainer
          error={payload.error}
          onBack={returnToBasket}
          paymentReversed={unitTotalPayableInclVat > 0}
        />
      );
    }

    if (
      isApiErrorCode(
        payload.error,
        ApiErrorCodesEnum.ORDER_VALIDATION_MINIMUM_AMOUNT_NOT_REACHED
      )
    ) {
      return (
        <ModalOrderMinimumContainer
          error={payload.error}
          onBack={returnToBasket}
          paymentReversed={unitTotalPayableInclVat > 0}
        />
      );
    }

    return (
      <ModalError onClose={returnToBasket}>
        <DisplayErrorMessage error={payload.error} />
      </ModalError>
    );
  }

  if (status === "pending-order-creation") {
    return <ModalLoadingGeneric />;
  }

  // For unattended payments, we don't verify signature
  if (status === "success" || status === "pending-signature-check") {
    if (hasReceiptModeAutomatic) {
      return (
        <Navigate
          to="/kiosk/order-confirmation/print"
          state={{ receiptList: payload?.receiptList }}
        />
      );
    }
    return (
      <Navigate
        to="/kiosk/order-confirmation"
        state={{ receiptList: payload?.receiptList }}
      />
    );
  }

  const formattedTotalAmount = priceFormatter({
    value: unitTotalPayableInclVat,
    currency,
    locale
  });

  return (
    <CardPaymentModal
      price={formattedTotalAmount}
      onCancel={debouncedOnCancel}
    />
  );
};
